import {
	CircleHelp,
	Clock,
	Maximize2,
	Minimize2,
	PenLine,
	Sparkles,
	X,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { IconButton } from "src/components/Default/LekUI/Button";
import { Tooltip } from "src/components/Default/LekUI/Tooltip";
import { ChatbotHistoryPopover } from "src/components/GlobalChatbot/ChatbotHistoryPopover";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";

interface ChatbotHeaderProps {
	isFullWidth: boolean;
	// brak = tryb bez full screen (mobile drawer)
	onToggleFullWidth?: () => void;
	activeConversationId: string | null;
	onSelectConversation: (conversationId: string) => void;
	onNewConversation: () => void;
	onOpenHistory: () => void;
	onClose: () => void;
}

export const ChatbotHeader = ({
	isFullWidth,
	onToggleFullWidth,
	activeConversationId,
	onSelectConversation,
	onNewConversation,
	onOpenHistory,
	onClose,
}: ChatbotHeaderProps) => {
	const { t } = useTranslation();
	const { openModal } = useModals();
	const [isHistoryOpen, setIsHistoryOpen] = useState(false);

	return (
		<div className="chatbot-header">
			<div className="chatbot-header-icon">
				<Sparkles className="icon-small" />
			</div>
			<span className="size-16 flex-1 m-0">
				{t("globalChatbot.title", "Tutor AI")}
			</span>

			{!isFullWidth && (
				<>
					<Tooltip
						title={t("globalChatbot.newConversation", "Nowa konwersacja")}
					>
						<IconButton
							data-test-id="global-chatbot-new-button"
							onClick={onNewConversation}
						>
							<PenLine className="icon-small" />
						</IconButton>
					</Tooltip>
					<Tooltip
						title={t(
							"globalChatbot.history.popoverTitle",
							"Historia konwersacji",
						)}
					>
						<IconButton
							data-test-id="global-chatbot-history-button"
							isActive={isHistoryOpen}
							onClick={() => setIsHistoryOpen((open) => !open)}
						>
							<Clock className="icon-small" />
						</IconButton>
					</Tooltip>
				</>
			)}

			{onToggleFullWidth && (
				<Tooltip
					title={
						isFullWidth
							? t("globalChatbot.collapse", "Zwiń do panelu")
							: t("globalChatbot.expand", "Rozszerz na całą szerokość")
					}
				>
					<IconButton
						data-test-id="global-chatbot-toggle-width-button"
						onClick={onToggleFullWidth}
					>
						{isFullWidth ? (
							<Minimize2 className="icon-small" />
						) : (
							<Maximize2 className="icon-small" />
						)}
					</IconButton>
				</Tooltip>
			)}

			<Tooltip title={t("globalChatbot.instructions.title", "Jak używać")}>
				<IconButton
					data-test-id="global-chatbot-instructions-button"
					onClick={() => openModal(ModalsNames.ChatbotInstructionsModal)}
				>
					<CircleHelp className="icon-small" />
				</IconButton>
			</Tooltip>

			<IconButton data-test-id="global-chatbot-close-button" onClick={onClose}>
				<X className="icon-small" />
			</IconButton>

			{isHistoryOpen && (
				<ChatbotHistoryPopover
					activeConversationId={activeConversationId}
					onSelect={(conversationId) => {
						setIsHistoryOpen(false);
						onSelectConversation(conversationId);
					}}
					onSeeAll={() => {
						setIsHistoryOpen(false);
						onOpenHistory();
					}}
					onClose={() => setIsHistoryOpen(false)}
				/>
			)}
		</div>
	);
};
